import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import { HashRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence, useInView, useAnimation } from "framer-motion";
import {
  Database,
  Users,
  Building2,
  Shield,
  Zap,
  Globe,
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  Briefcase,
  ChevronDown,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  PERSON_SCHEMA,
  ORG_SCHEMA,
  JOB_SCHEMA,
  TYPE_COLORS,
  CATEGORY_ACCENT,
  ICON_COLORS,
} from "@/schemaData";

const TypeBadge = ({ type }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${TYPE_COLORS[type] || "bg-white/5 text-white/50 border-white/10"}`}>
    {type}
  </span>
);

// ─── Sales Modal Context ───────────────────────────────────────────────────────

const SalesModalContext = createContext(() => {});
const useSalesModal = () => useContext(SalesModalContext);

const COMPANY_SIZES = [
  "1–10",
  "11–50",
  "51–200",
  "201–500",
  "501–1,000",
  "1,000+",
];

const SalesFormModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({ name: "", company: "", email: "", companySize: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Sales Inquiry — ${form.name} at ${form.company}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\nCompany Size: ${form.companySize}${form.message ? `\n\nMessage:\n${form.message}` : ""}`
    );
    window.location.href = `mailto:support@structurata.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { setForm({ name: "", company: "", email: "", companySize: "", message: "" }); setSubmitted(false); }, 300);
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-primary/60 focus:bg-white/8 transition-all duration-200";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative w-full max-w-lg glass-card border border-white/10 rounded-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/5">
              <div>
                <h2 className="text-lg font-semibold text-white">Talk to Sales</h2>
                <p className="text-sm text-white/40 mt-0.5">We'll get back to you within one business day.</p>
              </div>
              <button onClick={handleClose} className="text-white/30 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/5">
                <X size={18} />
              </button>
            </div>

            {submitted ? (
              <div className="px-6 py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Send size={22} className="text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">Your email client should open</h3>
                <p className="text-white/40 text-sm max-w-xs mx-auto">Your message has been pre-filled. Hit send and we'll be in touch shortly.</p>
                <Button onClick={handleClose} className="mt-6 rounded-full px-6 bg-white/10 hover:bg-white/15 text-white border-0">
                  Close
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5 font-medium">Name</label>
                    <input required value={form.name} onChange={set("name")} placeholder="Jane Smith" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5 font-medium">Company</label>
                    <input required value={form.company} onChange={set("company")} placeholder="Acme Corp" className={inputCls} />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-white/40 mb-1.5 font-medium">Work Email</label>
                  <input required type="email" value={form.email} onChange={set("email")} placeholder="jane@acme.com" className={inputCls} />
                </div>

                <div>
                  <label className="block text-xs text-white/40 mb-1.5 font-medium">Company Size</label>
                  <div className="relative">
                    <select
                      required
                      value={form.companySize}
                      onChange={set("companySize")}
                      className={`${inputCls} appearance-none pr-10 cursor-pointer`}
                      style={{ colorScheme: "dark" }}
                    >
                      <option value="" disabled>Select…</option>
                      {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s} employees</option>)}
                    </select>
                    <ChevronDown size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-white/40 mb-1.5 font-medium">
                    Message <span className="text-white/20">(optional)</span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us what you're building…"
                    rows={3}
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <div className="pt-1">
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl py-3 font-medium glow-blue glow-blue-hover"
                  >
                    Send Message
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                  <p className="text-center text-xs text-white/20 mt-3">
                    Opens your email client with the message pre-filled.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Animated Components ───────────────────────────────────────────────────────

const AnimatedCounter = ({ target, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(target);
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += Math.ceil(end / 50);
        if (start >= end) { setCount(end); clearInterval(timer); }
        else setCount(start);
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [isInView, target, duration]);

  return <span ref={ref} className="stat-number">{count}{suffix}</span>;
};

const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) controls.start("visible");
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, ease: "easeOut" } }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ─── Navbar ────────────────────────────────────────────────────────────────────

const Navbar = () => {
  const openSalesModal = useSalesModal();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      // Navigate home first, then scroll after page renders
      window.location.hash = "/";
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const navLinkCls = "text-muted-foreground hover:text-white transition-colors duration-200 cursor-pointer bg-transparent border-0 p-0 font-inherit text-inherit";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#/" className="logo-text text-xl md:text-2xl text-white" data-testid="logo">
            Structurata
          </a>

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("features")} className={navLinkCls}>
              Features
            </button>
            <a href="#/data-schema" className={navLinkCls}>
              Data Schema
            </a>
            <button onClick={openSalesModal} className={navLinkCls}>
              Contact
            </button>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 glow-blue glow-blue-hover btn-shine"
              data-testid="talk-to-sales-btn"
              onClick={openSalesModal}
            >
              Talk to Sales
            </Button>
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass border-t border-white/5 px-6 py-6"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4">
            <button onClick={() => scrollToSection("features")} className="text-muted-foreground hover:text-white py-2 text-left">Features</button>
            <a href="#/data-schema" onClick={() => setIsMobileMenuOpen(false)} className="text-muted-foreground hover:text-white py-2">Data Schema</a>
            <button onClick={() => { setIsMobileMenuOpen(false); openSalesModal(); }} className="text-muted-foreground hover:text-white py-2 text-left">Contact</button>
            <hr className="border-white/10" />
            <Button
              className="bg-primary hover:bg-primary/90 text-white rounded-full"
              onClick={openSalesModal}
            >
              Talk to Sales
            </Button>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// ─── Hero Data Visual ─────────────────────────────────────────────────────────

const PERSON_ROWS = [
  { label: "first_name", value: "Sarah", color: "text-emerald-400" },
  { label: "last_name", value: "Chen", color: "text-emerald-400" },
  { label: "headline", value: "VP Engineering · Stripe", color: "text-blue-300" },
  { label: "location", value: "San Francisco, CA", color: "text-blue-300" },
  { label: "industry", value: "Financial Technology", color: "text-blue-300" },
  { label: "connections", value: "3,841", color: "text-violet-300" },
  { label: "skills", value: '["Rust", "Distributed Systems", …]', color: "text-amber-300" },
  { label: "roles", value: "14 positions", color: "text-amber-300" },
  { label: "educations", value: "MIT · B.S. Computer Science", color: "text-amber-300" },
  { label: "is_available", value: "false", color: "text-rose-400" },
];

const ORG_ROWS = [
  { label: "name", value: "Stripe, Inc.", color: "text-emerald-400" },
  { label: "domain", value: "stripe.com", color: "text-blue-300" },
  { label: "industry", value: "Financial Technology", color: "text-blue-300" },
  { label: "employees_range", value: "1001–5000", color: "text-violet-300" },
  { label: "founded_date", value: "2010-09-01", color: "text-blue-300" },
  { label: "technologies", value: '["Ruby", "Go", "AWS", …+12]', color: "text-amber-300" },
  { label: "domain_traffic", value: "42,800,000", color: "text-violet-300" },
  { label: "funding_rounds", value: "Series I · $600M", color: "text-emerald-400" },
];

const DataRow = ({ label, value, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: 10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.35, delay }}
    className="flex items-baseline gap-2 py-1.5 border-b border-white/5 last:border-0"
  >
    <span className="text-xs font-mono text-white/35 w-28 flex-shrink-0 truncate">{label}</span>
    <span className={`text-xs font-mono truncate ${color}`}>{value}</span>
  </motion.div>
);

const HeroDataVisual = () => {
  const [activeTab, setActiveTab] = useState("person");
  const rows = activeTab === "person" ? PERSON_ROWS : ORG_ROWS;

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab(t => t === "person" ? "org" : "person");
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full h-[500px]">
      {/* Glow behind card */}
      <div className="absolute inset-0 rounded-3xl bg-primary/5 blur-3xl scale-95" />

      {/* Main card */}
      <div className="relative h-full rounded-2xl border border-white/10 glass-card overflow-hidden flex flex-col">

        {/* Terminal bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/3 flex-shrink-0">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          <div className="flex gap-1 ml-4">
            <button
              onClick={() => setActiveTab("person")}
              className={`text-xs font-mono px-3 py-1 rounded-md transition-all duration-200 ${activeTab === "person" ? "bg-primary/20 text-primary" : "text-white/30 hover:text-white/60"}`}
            >
              person
            </button>
            <button
              onClick={() => setActiveTab("org")}
              className={`text-xs font-mono px-3 py-1 rounded-md transition-all duration-200 ${activeTab === "org" ? "bg-primary/20 text-primary" : "text-white/30 hover:text-white/60"}`}
            >
              organization
            </button>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-mono text-white/25">live</span>
          </div>
        </div>

        {/* Record header */}
        <div className="px-4 pt-4 pb-3 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${activeTab === "person" ? "bg-blue-500/15" : "bg-purple-500/15"}`}>
                {activeTab === "person"
                  ? <Users size={16} className="text-blue-400" />
                  : <Building2 size={16} className="text-purple-400" />
                }
              </div>
              <div>
                <p className="text-xs text-white/35 font-mono">
                  {activeTab === "person" ? "person_data / v2.0.4" : "org_data / v2.0.0"}
                </p>
                <p className="text-sm font-semibold text-white">
                  {activeTab === "person" ? "record #482,917,443" : "record #18,203,871"}
                </p>
              </div>
            </div>
            <span className="text-xs font-mono text-emerald-400/80 border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 rounded">verified</span>
          </div>
        </div>

        {/* Data rows */}
        <div className="flex-1 overflow-hidden px-4 py-3">
          <motion.div key={activeTab} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
            {rows.map((row, i) => (
              <DataRow key={row.label} {...row} delay={i * 0.04} />
            ))}
          </motion.div>
        </div>

        {/* Footer stats */}
        <div className="flex-shrink-0 border-t border-white/5 px-4 py-3 grid grid-cols-3 gap-2">
          {activeTab === "person" ? (
            <>
              <div className="text-center">
                <p className="text-xs text-white/30 font-mono">records</p>
                <p className="text-sm font-bold text-white">850M+</p>
              </div>
              <div className="text-center border-x border-white/5">
                <p className="text-xs text-white/30 font-mono">fields</p>
                <p className="text-sm font-bold text-white">33</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-white/30 font-mono">accuracy</p>
                <p className="text-sm font-bold text-emerald-400">99%</p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center">
                <p className="text-xs text-white/30 font-mono">records</p>
                <p className="text-sm font-bold text-white">100M+</p>
              </div>
              <div className="text-center border-x border-white/5">
                <p className="text-xs text-white/30 font-mono">fields</p>
                <p className="text-sm font-bold text-white">29</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-white/30 font-mono">refresh</p>
                <p className="text-sm font-bold text-emerald-400">2.5w</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Hero Section ──────────────────────────────────────────────────────────────

const HeroSection = () => {
  const openSalesModal = useSalesModal();
  return (
    <section data-testid="hero-section" className="relative min-h-screen pt-32 pb-24 overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="glow-orb glow-orb-blue w-[600px] h-[600px] -top-40 -right-40" />
      <div className="glow-orb glow-orb-purple w-[400px] h-[400px] top-1/2 -left-40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="space-y-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary mb-6">
                <Database size={14} />
                Data Infrastructure
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]"
              data-testid="hero-title"
            >
              The Infrastructure for{" "}
              <span className="text-gradient">Global Data</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-lg"
              data-testid="hero-description"
            >
              Access the most comprehensive person and organization datasets.
              Built for enrichment platforms, data teams, and AI-driven workflows
              that demand accuracy at scale.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg font-medium glow-blue glow-blue-hover btn-shine"
                data-testid="hero-cta-primary"
                onClick={openSalesModal}
              >
                Talk to Sales
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/20 hover:bg-white/5 text-white rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm"
                data-testid="hero-cta-secondary"
                onClick={() => window.location.href = '#/data-schema'}
              >
                View Data Schema
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex items-center gap-6 pt-8"
            >
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 size={16} className="text-secondary" />
                <span>Enterprise ready</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 size={16} className="text-secondary" />
                <span>Global coverage</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <HeroDataVisual />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ─── Stats Section ─────────────────────────────────────────────────────────────

const StatsSection = () => {
  const stats = [
    { value: "850", suffix: "M+", label: "Person Records", icon: Users },
    { value: "100", suffix: "M+", label: "Organization Profiles", icon: Building2 },
    { value: "99", suffix: "%", label: "Data Accuracy", icon: Shield },
    { value: "2", suffix: ".5 weeks", label: "Refresh Cycle", icon: Zap },
  ];

  return (
    <section data-testid="stats-section" id="data" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="glow-orb glow-orb-green w-[500px] h-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary mb-4">
            <Database size={14} />
            Verified Data Points
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white">
            Billions of records,{" "}
            <span className="text-gradient">refreshed constantly</span>
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <AnimatedSection key={index} delay={index * 0.1}>
              <div
                className="glass-card border border-white/10 rounded-2xl p-6 md:p-8 text-center group hover:border-primary/30 transition-colors duration-300"
                data-testid={`stat-card-${index}`}
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <stat.icon size={24} className="text-primary" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-white mb-2">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Features Section ──────────────────────────────────────────────────────────

const FeaturesSection = () => {
  const features = [
    {
      icon: Users,
      title: "Person Data",
      description: "Comprehensive individual profiles with verified contact information, professional history, and social connections.",
      highlight: "850M+ profiles"
    },
    {
      icon: Building2,
      title: "Organization Data",
      description: "Detailed company records including firmographics, tech stack, funding history, and key decision makers.",
      highlight: "100M+ companies"
    },
    {
      icon: Globe,
      title: "Global Coverage",
      description: "Data spanning 190+ countries with regional accuracy and compliance with local data regulations.",
      highlight: "190+ countries"
    },
    {
      icon: Zap,
      title: "Real-time Updates",
      description: "Continuously refreshed datasets ensure you're always working with the most current information available.",
      highlight: "2.5 week refresh"
    },
    {
      icon: Shield,
      title: "Data Quality",
      description: "Multi-layer verification process ensures industry-leading accuracy rates across all data points.",
      highlight: "99% accuracy"
    },
    {
      icon: Database,
      title: "Bulk Exports",
      description: "Download complete datasets in multiple formats. Perfect for enrichment, ML training, and analytics.",
      highlight: "CSV, JSON, Parquet"
    },
  ];

  return (
    <section data-testid="features-section" id="features" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary mb-4">
            <Zap size={14} />
            Capabilities
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4">
            Everything you need for{" "}
            <span className="text-gradient">data excellence</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Purpose-built datasets that power the world's leading enrichment platforms,
            sales intelligence tools, and AI applications.
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <AnimatedSection key={index} delay={index * 0.1}>
              <div
                className="feature-card glass-card border border-white/10 rounded-2xl p-8 group hover:border-primary/30 transition-colors duration-300 h-full"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 mb-6 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <feature.icon size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">{feature.description}</p>
                <span className="inline-block text-sm font-mono text-secondary">{feature.highlight}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── CTA Section ───────────────────────────────────────────────────────────────

const CTASection = () => {
  const openSalesModal = useSalesModal();
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="glow-orb glow-orb-blue w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <AnimatedSection>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Ready to build with{" "}
            <span className="text-gradient">world-class data?</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
            Talk to our team and see why leading data teams trust Structurata
            for their most critical enrichment and AI workflows.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-6 text-lg font-medium glow-blue glow-blue-hover btn-shine"
              data-testid="cta-primary-btn"
              onClick={openSalesModal}
            >
              Talk to Sales
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ─── Footer ────────────────────────────────────────────────────────────────────

const Footer = () => {
  const openSalesModal = useSalesModal();
  const footerLinks = {
    product: [
      { label: "Datasets", onClick: openSalesModal },
      { label: "Person Data", onClick: openSalesModal },
      { label: "Organization Data", onClick: openSalesModal },
      { label: "Data Schema", href: "#/data-schema" },
    ],
    company: [
      { label: "About", href: "mailto:support@structurata.com" },
      { label: "Blog", href: "mailto:support@structurata.com" },
      { label: "Careers", href: "mailto:support@structurata.com" },
      { label: "Contact", href: "mailto:support@structurata.com" },
    ],
    resources: [
      { label: "Documentation", href: "mailto:support@structurata.com" },
      { label: "API Reference", href: "mailto:support@structurata.com" },
      { label: "Status", href: "mailto:support@structurata.com" },
      { label: "Changelog", href: "mailto:support@structurata.com" },
    ],
    legal: [
      { label: "Privacy", href: "mailto:support@structurata.com" },
      { label: "Terms", href: "mailto:support@structurata.com" },
      { label: "Security", href: "mailto:support@structurata.com" },
      { label: "Compliance", href: "mailto:support@structurata.com" },
    ],
  };

  return (
    <footer data-testid="footer" className="py-16 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          <div className="col-span-2 md:col-span-1">
            <a href="/" className="logo-text text-xl text-white mb-4 block">
              Structurata
            </a>
            <p className="text-sm text-muted-foreground">
              The infrastructure for global person and organization data.
            </p>
          </div>

          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                {category}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.onClick ? (
                      <button
                        onClick={link.onClick}
                        className="text-sm text-muted-foreground hover:text-white transition-colors duration-200"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm text-muted-foreground hover:text-white transition-colors duration-200"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Structurata. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white transition-colors duration-200">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white transition-colors duration-200">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </a>
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white transition-colors duration-200">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ─── Data Schema Page ──────────────────────────────────────────────────────────

const SchemaFieldRow = ({ field, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3, delay: index * 0.03 }}
    className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0 group"
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <code className="text-sm font-mono text-white/90 group-hover:text-white transition-colors">
          {field.name}
        </code>
        {field.required && (
          <span className="text-xs font-mono text-red-400/80 border border-red-500/20 bg-red-500/10 px-1.5 py-0.5 rounded">
            required
          </span>
        )}
        <TypeBadge type={field.type} />
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{field.description}</p>
    </div>
  </motion.div>
);

const SchemaCategoryCard = ({ category, delay = 0 }) => {
  const Icon = category.icon;
  const accent = CATEGORY_ACCENT[category.color] || CATEGORY_ACCENT.blue;
  const iconColor = ICON_COLORS[category.color] || ICON_COLORS.blue;

  return (
    <AnimatedSection delay={delay}>
      <div className={`rounded-2xl border p-6 h-full ${accent} backdrop-blur-sm`}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
            <Icon size={18} className={iconColor} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{category.name}</h3>
            <p className="text-xs text-muted-foreground">{category.fields.length} field{category.fields.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div>
          {category.fields.map((field, i) => (
            <SchemaFieldRow key={field.name} field={field} index={i} />
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const TABS = [
  {
    id: "person",
    label: "Person Data",
    icon: Users,
    schema: PERSON_SCHEMA,
    version: "v2.0.4",
    description: "850M+ individual profiles with verified professional and social data",
    totalFields: PERSON_SCHEMA.reduce((acc, c) => acc + c.fields.length, 0),
    color: "text-blue-400",
  },
  {
    id: "org",
    label: "Organization Data",
    icon: Building2,
    schema: ORG_SCHEMA,
    version: "v2.0.0",
    description: "100M+ company records with firmographics, funding, and tech stack",
    totalFields: ORG_SCHEMA.reduce((acc, c) => acc + c.fields.length, 0),
    color: "text-purple-400",
  },
  {
    id: "job",
    label: "Job Post Data",
    icon: Briefcase,
    schema: JOB_SCHEMA,
    version: "v2.0.0",
    description: "Active and historical job postings linked to organizations",
    totalFields: JOB_SCHEMA.reduce((acc, c) => acc + c.fields.length, 0),
    color: "text-emerald-400",
  },
];

const DataSchemaPage = () => {
  const openSalesModal = useSalesModal();
  const [activeTab, setActiveTab] = useState("person");
  const activeTabData = TABS.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-background text-foreground noise">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg" />
        <div className="glow-orb glow-orb-blue w-[500px] h-[500px] -top-40 left-1/2 -translate-x-1/2" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary mb-6">
              <Database size={14} />
              Data Schema
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-[1.1]"
          >
            Every field,{" "}
            <span className="text-gradient">documented</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-10"
          >
            Explore the complete schema for our Person, Organization, and Job Post data feeds.
            All fields are sourced, verified, and refreshed on a 2.5-week cycle.
          </motion.p>

          {/* Summary stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-6 mb-4"
          >
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <div key={tab.id} className="glass-card border border-white/10 rounded-2xl px-6 py-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                    <Icon size={20} className={tab.color} />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-muted-foreground">{tab.label}</p>
                    <p className="text-2xl font-bold text-white">{tab.totalFields} <span className="text-sm font-normal text-muted-foreground">fields</span></p>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="relative pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* Tab Bar */}
          <div className="flex gap-2 mb-10 p-1.5 glass-card border border-white/10 rounded-2xl w-fit mx-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-primary text-white shadow-lg"
                      : "text-muted-foreground hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon size={16} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>

          {/* Active tab header */}
          {activeTabData && (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-10"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
                <div>
                  <h2 className="text-2xl font-bold text-white">{activeTabData.label}</h2>
                  <p className="text-muted-foreground mt-1">{activeTabData.description}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs font-mono text-muted-foreground border border-white/10 bg-white/5 px-3 py-1.5 rounded-full">
                    {activeTabData.version}
                  </span>
                  <span className="text-xs font-mono text-primary border border-primary/20 bg-primary/10 px-3 py-1.5 rounded-full">
                    {activeTabData.totalFields} fields
                  </span>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-3 mb-8 p-4 glass-card border border-white/10 rounded-xl">
                <span className="text-xs text-muted-foreground mr-2 self-center">Types:</span>
                {[["string","string"],["integer","integer"],["boolean","boolean"],["string[]","string[]"],["object[]","object[]"],["date-time","date-time"]].map(([label, type]) => (
                  <TypeBadge key={type} type={type} />
                ))}
                <span className="ml-4 text-xs text-red-400/80 border border-red-500/20 bg-red-500/10 px-2 py-0.5 rounded font-mono">required</span>
              </div>

              {/* Schema grid */}
              <motion.div
                key={activeTab + "-grid"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="grid md:grid-cols-2 xl:grid-cols-3 gap-5"
              >
                {activeTabData.schema.map((category, i) => (
                  <SchemaCategoryCard key={category.name} category={category} delay={i * 0.05} />
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* CTA */}
          <AnimatedSection className="mt-16">
            <div className="glass-card border border-white/10 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="glow-orb glow-orb-blue w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              <div className="relative">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Ready to integrate this data?
                </h3>
                <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                  Talk to our team about data licensing, delivery formats, and integration support.
                </p>
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-6 text-lg font-medium glow-blue glow-blue-hover btn-shine"
                  onClick={openSalesModal}
                >
                  Talk to Sales
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// ─── Landing Page ──────────────────────────────────────────────────────────────

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground noise">
      <Navbar />
      <main>
        <HeroSection />
        <StatsSection />
        <FeaturesSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

// ─── App ───────────────────────────────────────────────────────────────────────

function App() {
  const [salesModalOpen, setSalesModalOpen] = useState(false);

  return (
    <SalesModalContext.Provider value={() => setSalesModalOpen(true)}>
      <HashRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/data-schema" element={<DataSchemaPage />} />
        </Routes>
        <SalesFormModal isOpen={salesModalOpen} onClose={() => setSalesModalOpen(false)} />
      </HashRouter>
    </SalesModalContext.Provider>
  );
}

export default App;
